import { IndustryStep } from '../components/IndustryStep';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GoalStep } from '../components/GoalStep';
import GrowegyLogo from '../../assets/images/growegy-logo.png';
import { VariablesStep } from '../components/VariablesStep';
import { PromptStep } from '../components/PromptStep';
import {
  ONBOARDING_STEP_END,
  ONBOARDING_STEP_GOAL,
  ONBOARDING_STEP_INDUSTRY,
  ONBOARDING_STEP_LAST,
  ONBOARDING_STEP_PROMPT,
  ONBOARDING_STEP_REFERRER,
  ONBOARDING_STEP_VARIABLES,
  ONBOARDING_STEP_SOCIAL_URLS,
  ONBOARDING_STEPS,
  OnboardingProgress,
  OnboardingStep,
  OnboardingStepAnswer,
} from '../types';
import { useUpdateOnboardingProgressMutation } from '../hooks/useUpdateOnboardingProgressMutation';
import {
  ONBOARDING_STATE_COMPLETED,
  ONBOARDING_STATE_IN_PROGRESS,
  ONBOARDING_STATE_SKIPPED,
  ONBOARDING_STEP_RESULT_COMPLETED,
  ONBOARDING_STEP_RESULT_SKIPPED,
  OnboardingState,
  OnboardingStepResult,
} from '../../user/userApi';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useAppDispatch } from '../../app/hooks';
import { setJustCompleted } from '../onboardingSlice';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../../router/AppRoutes';
import { useFetchedBillingCustomer } from '../../billing/hooks/useFetchedBillingCustomer';
import { SplashScreen } from '../../commons/SplashScreen';
import { nonBreakableSpace } from '../../util/utils';
import { ReferrerStep } from '../components/ReferrerStep';
import { SwitchUser } from '../../commons/components/SwitchUser';
import { SocialUrlsStep } from '../components/SocialUrlsStep';

type StepAndIndex = { name: OnboardingStep; index: number };

export type OnboardingWizardParams = { onboardingProgress: OnboardingProgress };
const OnboardingWizardInternal = (
  props: OnboardingWizardParams & { onboardingSteps: OnboardingStep[] }
) => {
  const { onboardingProgress, onboardingSteps } = props;
  const analytics = useAnalytics();

  const getNextStep = useCallback(
    (step: OnboardingStep) => {
      const currentStepIndex = onboardingSteps.indexOf(step);
      if (currentStepIndex < 0) return { name: onboardingSteps[0], index: 0 };
      if (currentStepIndex >= onboardingSteps.length - 1)
        return { name: step, index: currentStepIndex };
      return { name: onboardingSteps[currentStepIndex + 1], index: currentStepIndex + 1 };
    },
    [onboardingSteps]
  );

  const [step, setStep] = useState<StepAndIndex>(
    onboardingProgress.steps.length > 0
      ? getNextStep(
          onboardingProgress.steps[onboardingProgress.steps.length - 1].name as OnboardingStep
        )
      : { name: onboardingSteps[0], index: 0 }
  );
  const { mutate: updateOnboardingProgress } = useUpdateOnboardingProgressMutation();

  const navigate = useNavigate();
  useEffect(() => {
    navigate(AppRoutes.Home);
  }, [navigate]);

  const appDispatch = useAppDispatch();

  const advanceOnboarding = useCallback(
    (
      stepResult: OnboardingStepResult,
      answers?: OnboardingStepAnswer[],
      onboardingState?: OnboardingState
    ) => {
      const nextStep = getNextStep(step.name);
      setStep(nextStep);
      const newOnboardingState = onboardingState
        ? onboardingState
        : nextStep.name !== ONBOARDING_STEP_END
        ? ONBOARDING_STATE_IN_PROGRESS
        : ONBOARDING_STATE_COMPLETED;
      updateOnboardingProgress({
        state: newOnboardingState,
        steps: [
          ...onboardingProgress.steps,
          {
            name: step.name,
            result: stepResult,
            answers: answers ? answers : [],
            timestamp: new Date().toISOString(),
          },
        ],
      });
      if (newOnboardingState === ONBOARDING_STATE_COMPLETED) {
        analyticsTrack(analytics, AnalyticsEvent.ONBOARDING_COMPLETED);
        appDispatch(setJustCompleted({ justCompleted: true }));
      } else if (newOnboardingState === ONBOARDING_STATE_SKIPPED) {
        analyticsTrack(analytics, AnalyticsEvent.ONBOARDING_SKIPPED);
      }

      analyticsTrack(
        analytics,
        stepResult === ONBOARDING_STEP_RESULT_COMPLETED
          ? AnalyticsEvent.ONBOARDING_STEP_COMPLETED
          : AnalyticsEvent.ONBOARDING_STEP_SKIPPED,
        { step: step.name }
      );
    },
    [onboardingProgress.steps, step, updateOnboardingProgress, analytics, appDispatch, getNextStep]
  );

  const onCompleteStep = useCallback(
    (answers?: OnboardingStepAnswer[]) =>
      advanceOnboarding(ONBOARDING_STEP_RESULT_COMPLETED, answers),
    [advanceOnboarding]
  );

  const onSkipStep = useCallback(
    () => advanceOnboarding(ONBOARDING_STEP_RESULT_SKIPPED, []),
    [advanceOnboarding]
  );

  const onSkipWizard = useCallback(
    () => advanceOnboarding(ONBOARDING_STEP_RESULT_SKIPPED, [], ONBOARDING_STATE_SKIPPED),
    [advanceOnboarding]
  );

  const [hideHeader, setHideHeader] = useState(false);
  const onHideHeader = useCallback(() => setHideHeader(true), []);

  const stepProps = {
    stepIndex: step.index,
    totalSteps: onboardingSteps.length,
    onNext: onCompleteStep,
    onSkip: onSkipStep,
  };
  return (
    <div className="onboarding__container-wrapper">
      <div className="onboarding__container">
        {!hideHeader && (
          <div className="onboarding__header-container">
            <img src={GrowegyLogo} alt="Growegy logo" />{' '}
          </div>
        )}
        {step.index === 0 && (
          <div className="growegy-label-header--weight500">
            Welcome to Growegy!
            <br /> Please answer the following questions to personalize your
            {nonBreakableSpace}experience.
          </div>
        )}
        {step.name === ONBOARDING_STEP_REFERRER && <ReferrerStep {...stepProps} />}
        {step.name === ONBOARDING_STEP_INDUSTRY && <IndustryStep {...stepProps} />}
        {step.name === ONBOARDING_STEP_GOAL && <GoalStep {...stepProps} />}
        {step.name === ONBOARDING_STEP_SOCIAL_URLS && <SocialUrlsStep {...stepProps} />}
        {step.name === ONBOARDING_STEP_VARIABLES && <VariablesStep {...stepProps} />}
        {step.name === ONBOARDING_STEP_PROMPT && (
          <PromptStep {...stepProps} onHideHeader={onHideHeader} />
        )}
      </div>
      {step.name !== ONBOARDING_STEP_LAST && (
        <>
          <div className="onboarding__bottom-link-container growegy-title12">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a
              href="#"
              data-test="onboarding__skip-wizard"
              onClick={(e) => {
                e.preventDefault();
                onSkipWizard();
              }}
            >
              Skip setup and start using the app
            </a>
          </div>
          <div className="onboarding__bottom-link-container">
            <SwitchUser />
          </div>
        </>
      )}
    </div>
  );
};

export const OnboardingWizard = (props: OnboardingWizardParams) => {
  const {
    query: { data: customer, status: customerStatus },
    queryStatusComponent,
  } = useFetchedBillingCustomer();
  const onboardingSteps = useMemo(
    () =>
      customer?.isDirect !== undefined
        ? ONBOARDING_STEPS.filter((x) => x !== ONBOARDING_STEP_REFERRER || customer.isDirect)
        : null,
    [customer?.isDirect]
  );

  if (!onboardingSteps)
    return <SplashScreen queryStatus={customerStatus}>{queryStatusComponent}</SplashScreen>;
  return <OnboardingWizardInternal onboardingSteps={onboardingSteps} {...props} />;
};
